import { memo } from "react"
import { IconPlusOutline18 as Plus } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { isLooseGroup } from "@productlane/lib/src/help-center/loose-group"
import { Badge, IconButton, Tooltip } from "@productlane/ui"

import type { HiddenFolder, HiddenTier } from "./hidden-tier-folders"
import type { GroupWithArticles } from "./types"
import { RoleCheck } from "@/components/shared/role-check"
import { useDocNavStore } from "@/stores/docs/docs-nav-store"
import { useHelpCenterNav } from "./help-center-context"
import { HiddenArticleRow } from "./hidden-article-row"
import { hiddenFolderKey } from "./hidden-tier-folders"
import { NavDisclosureChevron } from "./nav-disclosure-chevron"
import { NavRowActions } from "./nav-row-actions"
import { translatedName } from "./translated-title"
import { useCreateArticle } from "./use-create-article"
import { useDelayedUnmount } from "./use-delayed-unmount"

// The folder an agent or internal article already sits in, rendered as a row of
// its own. Same shape as a public group, minus rename and delete: the group is
// shared with the public tree, so it is edited there.
export const HiddenFolderRow = memo(
	({
		folder,
		tier,
		articleGroups,
		addLabel,
	}: {
		folder: HiddenFolder
		tier: HiddenTier
		articleGroups: readonly GroupWithArticles[]
		addLabel: string
	}) => {
		const key = hiddenFolderKey(tier, folder.group.id)
		const isOpen = useDocNavStore((s) => s.openGroupIds.includes(key))
		const toggleGroup = useDocNavStore((s) => s.toggleGroup)
		const { selectedLanguage } = useHelpCenterNav()
		const createArticle = useCreateArticle({ articleGroups })
		const renderRows = useDelayedUnmount(isOpen, 250)

		const name = translatedName(folder.group, selectedLanguage)

		// Rows from top-level pages have no folder to sit in, here or on the site.
		if (isLooseGroup(folder.group)) {
			return (
				<li>
					<ul className="flex list-none flex-col gap-px pb-2">
						{folder.articles.map((article) => (
							<HiddenArticleRow
								key={article.id}
								article={article}
								articleGroups={articleGroups}
								tier={tier}
							/>
						))}
					</ul>
				</li>
			)
		}

		return (
			<li>
				<div
					role="button"
					tabIndex={0}
					onClick={(e) => {
						e.preventDefault()
						toggleGroup(key)
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault()
							toggleGroup(key)
						}
					}}
					className="group/item group relative flex h-[1.9rem] w-full cursor-default select-none items-center justify-between rounded-md pl-2 pr-1 text-sm hover:bg-gray-100 hover:text-primary hover:dark:bg-gray-800/50"
				>
					<div className="min-w-0 flex-1">
						<div className="truncate text-left font-medium text-secondary opacity-90 group-hover:text-primary">
							{name}
						</div>
					</div>
					<div className="flex flex-none items-center gap-1">
						<Badge
							variant="light-gray"
							size="sm"
							className="pointer-events-none select-none group-hover:opacity-0"
						>
							{folder.articles.length > 99 ? "99+" : folder.articles.length}
						</Badge>
						<NavDisclosureChevron isOpen={isOpen} />
					</div>
					<RoleCheck not={["VIEWER"]}>
						{/* 28px = pr-1 + the chevron's 24px box, so the strip meets the
						chevron and the name has no gap to show through. */}
						<NavRowActions className="right-7">
							<Tooltip label={addLabel}>
								<IconButton
									Icon={Plus}
									variant="ghost"
									size="md"
									type="button"
									aria-label={addLabel}
									iconStrokeWidth={1.75}
									iconClassName="h-3 w-3"
									className="relative text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
									onClick={(e) => {
										e.preventDefault()
										e.stopPropagation()
										createArticle({
											visibility: tier,
											groupId: folder.group.id,
										})
									}}
								/>
							</Tooltip>
						</NavRowActions>
					</RoleCheck>
				</div>
				<div
					inert={!isOpen}
					className={cn(
						"ease-[cubic-bezier(0.23,1,0.32,1)] grid transition-[grid-template-rows,opacity] duration-200",
						isOpen
							? "grid-rows-[1fr] opacity-100"
							: "grid-rows-[0fr] opacity-40",
					)}
				>
					<div className="min-h-0 overflow-hidden">
						<ul className="flex list-none flex-col gap-px pb-2 pt-1">
							{renderRows &&
								folder.articles.map((article) => (
									<HiddenArticleRow
										key={article.id}
										article={article}
										articleGroups={articleGroups}
										tier={tier}
									/>
								))}
						</ul>
					</div>
				</div>
			</li>
		)
	},
)

HiddenFolderRow.displayName = "HiddenFolderRow"
